/**
 * THE SOURCE BEHIND A FIGURE, OPENABLE FROM THE REPORT ITSELF.
 *
 * WHY THIS EXISTS
 *   `components/SourceDrawer.tsx` and `components/ProvenanceChip.tsx` were built, tested and
 *   unreachable. Nothing in the CMA flow rendered either of them, so the three-level disclosure the
 *   product's whole promise rests on — the figure, where it came from, and the record underneath —
 *   existed only in a mock fixture. A reader of a real brief could not open the source behind
 *   anything. This is the wiring, and it is deliberately the SMALLEST honest one.
 *
 * WHY THE AREA FACTS AND NOT THE HEADLINE FIGURE
 *   `SourceDrawer` takes a `Field` (types/recommendation.ts), and a `Field` is REQUIRED to carry a
 *   tier — the drawer reads `field.tier` to style its chip and to decide whether to print the
 *   "you entered this" and "not found" explanations. Exactly one block of `AmbReportData` states a
 *   tier per figure: `area_facts.facts[]`, where `tier` is written by
 *   `providers/location/models.AreaFact` and carried through `amb_build._area_facts_block`
 *   untouched, alongside a named source, a resolvable URL, a vintage, a geo key and the basis on
 *   which the fact was attached to this address.
 *
 *   Every other figure on this payload — the estimate, the range, the suggested list, each
 *   comparable's adjusted price — arrives as a rendered DISPLAY STRING and nothing else. There is
 *   no tier beside it, no record id, no accessed date, no underlying rows. Picking a tier for one
 *   of them here would be the client classifying valuation data, which is exactly what ADR-0002
 *   forbids: React renders what the server sends and decides nothing about it. So the drawer is
 *   wired where the server genuinely answers the drawer's question, and nowhere else. The gap is
 *   named in the report that accompanies this change rather than papered over with a default tier.
 *
 * WHAT A READER SEES WHEN THE PROVIDER IS OFF
 *   Nothing at all, which is the same rule `engine/amb_render._area_facts_html` follows: no
 *   heading, no empty table, no "not available" row. The area-facts provider ships inert
 *   (`VALUBRIEF_AREA_FACTS_ENABLED` and `VALUBRIEF_AREA_FACTS_DELIVERABLE_LICENSE` both default to
 *   0), so on a deployment that has not turned it on this section renders on no brief — the same
 *   posture `PhotoSourceNote` takes, and the reason the omission rule is asserted in the tests.
 */
import { ProvenanceChip } from "../components/ProvenanceChip";
import type { Field, Tier } from "../types/recommendation";
import { themeVar as colors } from "../design-tokens";
import type { AmbReportData } from "./types";

/** One entry of `area_facts.facts[]`, named off the contract rather than re-declared here, so this
 *  file cannot drift from `cma/types.ts` the way a hand-copied interface would. */
export type AreaFact = AmbReportData["area_facts"]["facts"][number];

/** The tier tokens `design-tokens.tierStyles` has a style for. This is a CAPABILITY check — "can
 *  this client render the tier the server stated?" — and never a classification: a token that is
 *  not in this list is not mapped to a nearby one, it means no chip is offered at all. */
const RENDERABLE_TIERS: readonly string[] = [
  "verified",
  "indicative",
  "gap",
  "assumption",
  "entered",
];

/**
 * One area fact, expressed in the shape the drawer already reads. Every value below is COPIED, and
 * the mapping is a rename of the server's own keys — nothing here computes, reformats or infers.
 * Exported because the mapping, not the markup, is the part worth asserting in a test.
 *
 * Returns `null` when the server stated a tier this client has no style for. That case renders as
 * the plain label and value with no chip, because a chip whose tier we guessed would be a claim
 * the server never made.
 *
 * TWO FIELDS OF THE CONTRACT ARE DELIBERATELY LEFT UNSET:
 *   `accessDate` — the drawer prints it as "Accessed", and `vintage` is not an access date. It is
 *   the vintage of the DATA ("ACS 2019-2023", "OSM snapshot 2026-06"), which is a different claim
 *   and is printed beside the chip instead, where both deliverable renderers already print it.
 *   `reason` — required by the contract only for a `gap`, and an area fact is verified or it does
 *   not exist (`AreaFact.tier`'s own docstring), so there is nothing honest to put there.
 */
export function areaFactField(fact: AreaFact): Field | null {
  const tier = typeof fact?.tier === "string" ? fact.tier : "";
  if (!RENDERABLE_TIERS.includes(tier)) return null;
  return {
    id: fact.id,
    label: fact.label,
    display: fact.value,
    tier: tier as Tier,
    source: fact.source,
    // The record this fact was read FOR: the 12-digit Census block-group GEOID the address
    // resolved to. It is the one identifier that makes the fact re-checkable against the source.
    recordId: fact.geo_key,
    // L2. `geo_basis` is the server's own sentence for how this fact came to be attached to this
    // address — "geocode -> NCES attendance -> TEA", "B19013 for GEOID". That is a derivation, so
    // it belongs in the drawer's "How it was computed" section rather than being dropped; the
    // string is passed through verbatim and never reworded here.
    state: typeof fact.geo_basis === "string" && fact.geo_basis ? fact.geo_basis : undefined,
  };
}

/**
 * The "Area and schools" block of the inline report, with each fact's provenance one click away.
 *
 * Placed at the same point in the reading order the deliverable puts it — after the disclosures,
 * before nothing — because the on-screen brief and the PDF an agent signs should not present the
 * same material in two different orders. The screen and the document disagreeing about what comes
 * where is how an agent proof-reads one and ships the other.
 */
export function AreaFactsSection({ data }: { data: AmbReportData }) {
  const block = data?.area_facts;
  // The omission rule, copied from `_area_facts_html`: disabled, absent or zero confirmed facts
  // renders NOTHING — no heading and no empty state. A section that appears with nothing in it
  // reads as a broken feature rather than as one that was correctly not used.
  if (!block || !block.enabled) return null;
  const facts = Array.isArray(block.facts) ? block.facts : [];
  if (facts.length === 0) return null;

  const sources = Array.isArray(block.sources_used) ? block.sources_used : [];

  return (
    <section style={{ marginTop: 28, paddingTop: 14, borderTop: `1px solid ${colors.border}` }}>
      <h2 style={{ fontSize: 15 }}>Area and schools</h2>

      {/* The paragraph is composed SERVER-side, literally, by concatenating each confirmed fact's
          own label, value, source and vintage into a fixed template — `_area_facts_block` is
          explicit that it is never handed to a model. It is rendered as React text rather than
          through this screen's raw-HTML sink: it carries no markup by construction, and the agent
          note below it is verbatim human prose, which never goes near that sink (the same rule
          `data.note` follows). */}
      {block.platform_paragraph && (
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 6 }}>{block.platform_paragraph}</p>
      )}

      <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
        {facts.map((fact, i) => {
          const field = areaFactField(fact);
          return (
            <div
              key={fact?.id || i}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                gap: 8,
                fontSize: 13,
                borderBottom: `1px solid ${colors.border}`,
                padding: "5px 0",
              }}
            >
              <span style={{ color: colors.textSecondary, minWidth: 220 }}>{fact.label}</span>
              {field ? (
                // L1: the value, its tier, and the affordance that opens the drawer. The chip is
                // the component that already exists for this; a second one would be a second
                // vocabulary for the same idea, which is the reconcile note FactChip.tsx opens with.
                <ProvenanceChip field={field} />
              ) : (
                // The server stated a tier this client cannot style. Show the figure, say nothing
                // about how sure anyone is of it, and offer no drawer — see `areaFactField`.
                <span style={{ fontFamily: "monospace" }}>{fact.value}</span>
              )}
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                {/* The same tag the PDF and the Word file print beside the value: the named source
                    and the vintage of the data, in the server's own words. */}
                {fact.source}
                {fact.vintage ? `, ${fact.vintage}` : ""}
              </span>
              {fact.source_url && (
                <a
                  href={fact.source_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ fontSize: 12, color: colors.accent }}
                >
                  open source
                </a>
              )}
              {fact.attribution && (
                // The provider's required credit, carried per fact because that is how the payload
                // carries it — OpenStreetMap's ODbL line rides on the OSM fact and on no other.
                <span style={{ fontSize: 12, color: colors.textSecondary }}>{fact.attribution}</span>
              )}
            </div>
          );
        })}
      </div>

      {block.agent_note && (
        <p style={{ fontSize: 13, lineHeight: 1.6, marginTop: 10, color: colors.textPrimary }}>
          In the agent&rsquo;s words: {block.agent_note}
        </p>
      )}

      {sources.length > 0 && (
        <p style={{ fontSize: 12, color: colors.textSecondary, marginTop: 8 }}>
          Sources: {sources.join(", ")}
        </p>
      )}
    </section>
  );
}

export default AreaFactsSection;
